import { useCommonModalConfig } from '@hclinear/hc-linear-react';
import { type AnnouncementItemDTO } from '@model/announcement/AnnouncementItemDTO';
import { useEffect, useState } from 'react';
import { type AnnouncementRangeDTO } from '@/model';

interface UseAnnouncementRangeProps {
  existingItem: AnnouncementItemDTO | undefined;
  onDirty: () => void;
}

// The range modal only ever collects/submits the range-specific fields
// (see AnnouncementItemRangeModal's form fields) — never the item's own
// id/announcementId/name/content/render fields, and never
// announcementTiming either (nothing in that modal edits it). Cleared here
// before every submission overlay so switching between INTERVAL and
// PERIODS doesn't leave the other type's now-irrelevant fields (days,
// dateFrom, timeFrom, ...) lingering in the merged draft.
const SCHEDULE_FIELDS_TO_RESET: Partial<AnnouncementRangeDTO> = {
  dateTimeFrom: undefined,
  dateTimeTo: undefined,
  dateFrom: undefined,
  dateTo: undefined,
  timeFrom: undefined,
  timeTo: undefined,
  days: undefined,
};

interface UseAnnouncementRangeResult {
  // rangeDraft is seeded from existingItem and kept in sync on every range-
  // modal submit, so it — not existingItem — is the current source of
  // truth; falling back to existingItem instead would silently ignore edits
  // made through the modal for an item that already existed. baseItem is
  // the same value, named for how the save flow actually uses it: the item
  // to build the save payload from.
  baseItem: AnnouncementItemDTO | undefined;
  announcementRange?: AnnouncementRangeDTO;
  announcementRangeModalOpen: boolean;
  onAnnouncementRangeModalOpen: () => void;
  onAnnouncementRangeModalClose: () => void;
  onAnnouncementRangeSubmit: (range: AnnouncementRangeDTO) => void;
  // Called once a brand-new item's first save comes back with the id it was
  // saved under, so baseItem carries that id from then on — otherwise every
  // further save would still go out without an id and create another item.
  onItemSaved: (id: number) => void;
}

export const useAnnouncementRange = ({
  existingItem,
  onDirty,
}: UseAnnouncementRangeProps): UseAnnouncementRangeResult => {
  // A brand-new item has no stop assignment or scheduling yet — that's
  // collected by the range modal instead of the backend's own defaults,
  // since it rejects an item saved without them (dateTimeFrom, stopIds).
  const [rangeDraft, setRangeDraft] = useState<AnnouncementRangeDTO | undefined>(
    existingItem,
  );
  const {
    modalConfig: rangeModalConfig,
    onModalOpen: onRangeModalOpen,
    onModalClose: onRangeModalClose,
  } = useCommonModalConfig();

  useEffect(() => {
    if (!existingItem) {
      onRangeModalOpen();
    }
    // Only ever prompted once, right when a not-yet-existing item is opened.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    baseItem: rangeDraft,
    announcementRange: rangeDraft,
    announcementRangeModalOpen: rangeModalConfig.open,
    onAnnouncementRangeModalOpen: onRangeModalOpen,
    onAnnouncementRangeModalClose: onRangeModalClose,
    onAnnouncementRangeSubmit: (range) => {
      setRangeDraft((prev) => ({
        ...prev,
        //This for excluding dependent field after type switch
        ...SCHEDULE_FIELDS_TO_RESET,
        ...range,
      }));
      onDirty();
      onRangeModalClose();
    },
    onItemSaved: (id) => {
      setRangeDraft((prev) => (prev ? { ...prev, id } : prev));
    },
  };
};
